type FeeLike = {
  readonly name?: string | null;
  readonly totalCents?: number | null;
};

type CartLike = {
  readonly fees?: ReadonlyArray<FeeLike | null> | null;
  readonly totalCents?: number | null;
};

type ServerOrderSurchargesInput = {
  readonly enablePosOrderSurchargesAsFees: boolean;
  readonly fees?: ReadonlyArray<FeeLike | null> | null;
};

type CartTotalWithServerOrderSurchargesInput = {
  readonly cart?: CartLike | null;
  readonly enablePosOrderSurchargesAsFees: boolean;
};

type CartWithServerOrderSurchargesInput<Cart extends CartLike | null | undefined> = {
  readonly cart: Cart;
  readonly enablePosOrderSurchargesAsFees: boolean;
};

const BAG_SURCHARGE_NAMES = new Set(['bag_fee', 'bag_surcharge']);

export const normalizeSurchargeName = (name?: string | null) =>
  (name ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const isBagSurcharge = (fee?: FeeLike | null) =>
  BAG_SURCHARGE_NAMES.has(normalizeSurchargeName(fee?.name));

export const getBagSurchargeCents = (fees?: ReadonlyArray<FeeLike | null> | null) =>
  (fees ?? []).reduce((total, fee) => {
    if (!isBagSurcharge(fee)) {
      return total;
    }

    return total + (fee?.totalCents ?? 0);
  }, 0);

export const getServerOrderSurchargesCents = ({
  enablePosOrderSurchargesAsFees,
  fees,
}: ServerOrderSurchargesInput) => {
  if (!enablePosOrderSurchargesAsFees) {
    return 0;
  }

  return getBagSurchargeCents(fees);
};

export const getCartTotalCentsWithServerOrderSurcharges = ({
  cart,
  enablePosOrderSurchargesAsFees,
}: CartTotalWithServerOrderSurchargesInput) => {
  return (
    (cart?.totalCents ?? 0) +
    getServerOrderSurchargesCents({
      enablePosOrderSurchargesAsFees,
      fees: cart?.fees,
    })
  );
};

export const getCartWithServerOrderSurcharges = <Cart extends CartLike | null | undefined>({
  cart,
  enablePosOrderSurchargesAsFees,
}: CartWithServerOrderSurchargesInput<Cart>): Cart => {
  if (!cart) {
    return cart;
  }

  return {
    ...cart,
    totalCents: getCartTotalCentsWithServerOrderSurcharges({
      cart,
      enablePosOrderSurchargesAsFees,
    }),
  } as Cart;
};
